import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';
import orderBy from 'lodash.orderby';
import cloneDeep from 'lodash.clonedeep';
import { format, parseISO } from 'date-fns';
import TableContainer from '@mui/material/TableContainer';
import RadioGroup from '@mui/material/RadioGroup';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Table } from '@components/Table';
import ActionIconButton from '@components/ActionIconButton';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { ProfileName, ProfileType } from '@constants/integrations';
import { COMPANY_STATUS, ChatType, locatedInRelationTypeLabelKey, googleMapsPlaceUrl } from '@constants/companies';
import { SyncDiff, GOOGLE_RESTRICTED_FIELD_REASON, GOOGLE_DESCRIPTION_URL_REASON } from '@constants/profiles';
import { profilesService } from '@services';
import { accountActions, alertActions } from '@actions';
import { I18N_DATE_FORMAT } from '@helpers/dates';
import { formatPhoneNumber } from '@helpers/phone';
import { handleApiError } from '@helpers/api';
import { findProfileById } from '@helpers/profileSync';
import { RESOLVE_ORIGIN as ORIGIN, seedResolveSelections, combineResolveSelections } from '@helpers/profileResolve';
import { Modal, Image, Text, CellWithRadioGroup, RadioSubtitle, StyledRadio, StyledFormControlLabel } from './styles';

const getTableHeader = (t, platform) => {
    return [
        {
            key: 0,
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('ProfilesPage.field')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    Pluspoint
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {platform}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.category', { count: 1 })}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.action')}
                </Typography>
            ),
        },
    ];
};

const renderValueList = (values, { format = value => value, comparedTo } = {}) =>
    values.length === 0 ? (
        <Text variant="body2" align="left">
            -
        </Text>
    ) : (
        values.map((value, index) => (
            <Text
                key={index}
                variant="body2"
                align="left"
                hasDiff={comparedTo ? !comparedTo.includes(value) : undefined}
            >
                {format(value)}
            </Text>
        ))
    );

const getRejectionHint = (rejection, t, platform) => {
    if (!rejection) {
        return null;
    }
    return rejection.reasons?.includes(GOOGLE_DESCRIPTION_URL_REASON)
        ? t('ProfilesPage.descriptionContainsUrlHint')
        : t('ProfilesPage.rejectedFieldHint', { source: platform, reasons: rejection.reasons?.join(', ') });
};

const getTableBody = (
    data,
    platform,
    selectedData,
    setSelectedData,
    fbConnected,
    restrictedFields,
    pushRejections,
    t,
) => {
    return data.map(item => {
        const isFbOrInstaLink = item.action === SyncDiff.Facebook || item.action === SyncDiff.Instagram;
        const isGoogle = platform === ProfileType[ProfileName.GoogleOfficial];
        const isStatus = item.action === SyncDiff.Status;
        const isRestricted = restrictedFields.includes(item.action);
        const unsendableHint = item.unsendableHint;
        const rejectionHint = getRejectionHint(
            pushRejections.find(rejection => rejection.field === item.action),
            t,
            platform,
        );
        const syncLabel = (
            <div>
                <Typography variant="body2">{t('ProfilesPage.usePlatformValue', { source: platform })}</Typography>
                <RadioSubtitle variant="caption">
                    {t('ProfilesPage.updatePlatformToMatch', { source: platform, target: 'Pluspoint' })}
                </RadioSubtitle>
            </div>
        );
        return [
            {
                component: (
                    <Typography variant="body2" align="left">
                        {item.field}
                    </Typography>
                ),
            },
            {
                component: <div style={{ minWidth: '200px', padding: '6px 0' }}>{item.plsp}</div>,
            },
            {
                component: <div style={{ minWidth: '200px', padding: '6px 0' }}>{item.platform}</div>,
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {item.category}
                    </Typography>
                ),
            },
            {
                component: (
                    <CellWithRadioGroup>
                        <RadioGroup
                            value={selectedData[item.action] === undefined ? '' : selectedData[item.action]}
                            onChange={e =>
                                setSelectedData(prev => ({ ...prev, [item.action]: parseInt(e.target.value) }))
                            }
                        >
                            {isRestricted && (
                                <RadioSubtitle variant="caption" sx={{ marginBottom: '8px' }}>
                                    {t('ProfilesPage.restrictedFieldHint', { source: platform })}
                                </RadioSubtitle>
                            )}
                            {!isRestricted && rejectionHint && (
                                <RadioSubtitle variant="caption" sx={{ marginBottom: '8px' }}>
                                    {rejectionHint}
                                </RadioSubtitle>
                            )}
                            {!isRestricted && !rejectionHint && unsendableHint && (
                                <RadioSubtitle variant="caption" sx={{ marginBottom: '8px' }}>
                                    {unsendableHint}
                                </RadioSubtitle>
                            )}
                            {!isStatus && !isRestricted && !unsendableHint && (
                                <StyledFormControlLabel
                                    value={ORIGIN.LOCAL}
                                    control={<StyledRadio />}
                                    label={
                                        <div>
                                            <Typography variant="body2">
                                                {t('ProfilesPage.usePlatformValue', { source: 'Pluspoint' })}
                                            </Typography>
                                            <RadioSubtitle variant="caption">
                                                {t('ProfilesPage.updatePlatformToMatch', {
                                                    source: 'Pluspoint',
                                                    target: platform,
                                                })}
                                            </RadioSubtitle>
                                        </div>
                                    }
                                    sx={{ alignItems: 'flex-start', marginBottom: '8px' }}
                                />
                            )}
                            {!(isFbOrInstaLink && isGoogle && fbConnected) && !item.pluspointOnly && (
                                <StyledFormControlLabel
                                    value={ORIGIN.SYNC}
                                    control={<StyledRadio />}
                                    label={syncLabel}
                                    sx={{ alignItems: 'flex-start', marginBottom: '8px' }}
                                />
                            )}
                            {!item.notIgnorable && (
                                <StyledFormControlLabel
                                    value={ORIGIN.IGNORE}
                                    control={<StyledRadio />}
                                    label={
                                        <div>
                                            <Typography variant="body2">{t('ProfilesPage.ignore')}</Typography>
                                            <RadioSubtitle variant="caption">
                                                {t('ProfilesPage.noChangesWillBeMade')}
                                            </RadioSubtitle>
                                        </div>
                                    }
                                    sx={{ alignItems: 'flex-start' }}
                                />
                            )}
                        </RadioGroup>
                    </CellWithRadioGroup>
                ),
            },
        ];
    });
};

const NO_HOURS = [];

const ResolveModal = ({ isOpen, handleClose, profileId, onRefetchData }) => {
    const [selectedData, setSelectedData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { data } = useSelector(state => state.profiles);
    const language = useSelector(state => state.auth.user?.language);
    const locations = useSelector(state => state.account?.account?.companies || []);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const profile = findProfileById(data, profileId);
    const plspData = profile?.locationData;
    const syncData = profile?.syncData;
    const plspHours = plspData?.workingHours ?? NO_HOURS;
    const syncHours = syncData?.workingHours ?? NO_HOURS;
    const currentDiff = profile?.syncDiff ?? [];
    const differingAddressParts = profile?.differingAddressParts ?? [];
    const seededData = seedResolveSelections(
        currentDiff,
        (profile?.ignoredSyncFields ?? []).filter(field => field !== SyncDiff.ParentLocation),
    );
    const isResolvable = Boolean(plspData && syncData);

    useEffect(() => {
        setSelectedData(seededData);
    }, [profileId]);

    useEffect(() => {
        if (!isResolvable) {
            handleClose();
        }
    }, [isResolvable, handleClose]);

    const fbConnected =
        profile?.locations?.length === 1 &&
        locations
            .find(l => l.companyId === profile?.locations[0])
            ?.profiles.some(p => p.type === ProfileName.FacebookOfficial);
    const isGoogle = profile?.platform === ProfileName.GoogleOfficial;

    const category = {
        1: ['locationName'],
        2: ['status'],
        3: ['phoneNumber', 'phoneExtension'],
        4: ['email'],
        5: ['website'],
        6: [
            'hasBusinessAddress',
            'latitude',
            'longitude',
            'addressLine1',
            'addressLine2',
            'city',
            'region',
            'postalCode',
        ],
        7: ['workingHours', 'workingHoursEnabled'],
        8: ['description'],
        9: ['openingDate'],
        10: ['logoUrl'],
        11: ['coverPhotoUrl'],
        12: ['socialMediaLinks.Facebook'],
        13: ['socialMediaLinks.Instagram'],
        14: ['socialMediaLinks.Youtube'],
        15: ['socialMediaLinks.X'],
        16: ['socialMediaLinks.LinkedIn'],
        17: ['socialMediaLinks.Tiktok'],
        18: ['socialMediaLinks.Pinterest'],
        19: ['chatLink', 'chatType'],
        20: ['primaryCategory'],
        21: ['secondaryCategories'],
        22: ['specialHours'],
        25: ['additionalPhones'],
    };
    const workingDays = useMemo(() => {
        return [...new Set([...plspHours.map(({ day }) => day), ...syncHours.map(({ day }) => day)])];
    }, [plspHours, syncHours]);

    if (!isResolvable) {
        return null;
    }

    const plslWorkingHours = plspHours.reduce((acc, { day, hours }) => ({ ...acc, [day]: hours }), {});
    // Facebook publishes a schedule it can build no periods from as always-open, so there is no way
    // to tell it the location has no hours — the server refuses that choice for the same reason.
    const hoursAreSendable = plspHours.some(({ hours }) => (hours || []).length > 0);
    const syncWorkingHours = syncHours.reduce((acc, { day, hours }) => ({ ...acc, [day]: hours }), {});

    const plslSpecialHours = (plspData.specialHours || []).reduce((acc, hour) => ({ ...acc, [hour.date]: hour }), {});
    const formatSpecialHour = hour =>
        hour.isClosed
            ? t('common.closed')
            : [...(hour.hours || [])]
                  .sort((a, b) => (a.from || '').localeCompare(b.from || ''))
                  .map(h => `${h.from}-${h.to}`)
                  .join(', ');
    const DAY_NAMES = {
        0: t('common.sunday'),
        1: t('common.monday'),
        2: t('common.tuesday'),
        3: t('common.wednesday'),
        4: t('common.thursday'),
        5: t('common.friday'),
        6: t('common.saturday'),
    };

    const renderLocatedIn = parentLocation =>
        parentLocation?.placeId ? (
            <>
                {`${parentLocation.title || '-'} (${t(locatedInRelationTypeLabelKey(parentLocation.relationType))})`}
                <ActionIconButton
                    title={t('SettingsCompanies.viewOnGoogleMaps')}
                    onClick={() =>
                        window.open(googleMapsPlaceUrl(parentLocation.placeId), '_blank', 'noopener,noreferrer')
                    }
                >
                    <OpenInNewIcon />
                </ActionIconButton>
            </>
        ) : (
            '-'
        );
    const sameParentLocation =
        plspData.parentLocation?.placeId === syncData.parentLocation?.placeId &&
        plspData.parentLocation?.relationType === syncData.parentLocation?.relationType;

    const plspPrimaryCategory = plspData.categories?.find(category => category.isPrimary);
    const syncPrimaryCategory = syncData.categories?.find(category => category.isPrimary);
    const plspAdditionalCategories = orderBy(
        plspData.categories?.filter(category => !category.isPrimary).map(category => category.displayName),
    );
    const syncAdditionalCategories = orderBy(
        syncData.categories?.filter(category => !category.isPrimary).map(category => category.displayName),
    );
    const plspAdditionalPhones = plspData.additionalPhones ?? [];
    const syncAdditionalPhones = syncData.additionalPhones ?? [];

    const API = {
        [ProfileName.GoogleOfficial]: profilesService.resolveConflictsGoogle,
        [ProfileName.FacebookOfficial]: profilesService.resolveConflictsFacebook,
    };

    const onSubmit = async () => {
        try {
            setIsSubmitting(true);

            const activeSelectedData = combineResolveSelections({
                selections: selectedData,
                currentDiff,
            });
            const resolvedData = cloneDeep(plspData);
            for (const [cat, fields] of Object.entries(category)) {
                const categoryValue = activeSelectedData[cat];

                if (categoryValue === ORIGIN.SYNC) {
                    fields.forEach(field => {
                        const fieldParts = field.split('.');

                        if (fieldParts.length === 1) {
                            resolvedData[field] = syncData[field];
                        } else if (fieldParts.length === 2) {
                            const [outerField, innerField] = fieldParts;
                            if (!resolvedData[outerField]) {
                                resolvedData[outerField] = {};
                            }
                            resolvedData[outerField][innerField] = syncData[outerField][innerField];
                        }
                    });
                }
            }

            // Handle categories specially - they use an array structure, not individual fields
            // The category mapping uses 'primaryCategory' and 'secondaryCategories' which don't exist
            // as fields - the actual data is in the 'categories' array with isPrimary flag
            if (
                activeSelectedData[SyncDiff.PrimaryCategory] !== undefined ||
                activeSelectedData[SyncDiff.SecondaryCategories] !== undefined
            ) {
                const plspPrimary = plspData.categories?.find(c => c.isPrimary);
                const plspSecondary = plspData.categories?.filter(c => !c.isPrimary) || [];
                const syncPrimary = syncData.categories?.find(c => c.isPrimary);
                const syncSecondary = syncData.categories?.filter(c => !c.isPrimary) || [];

                const primaryChoice = activeSelectedData[SyncDiff.PrimaryCategory];
                const primary =
                    primaryChoice === ORIGIN.SYNC || primaryChoice === ORIGIN.IGNORE ? syncPrimary : plspPrimary;

                const secondaryChoice = activeSelectedData[SyncDiff.SecondaryCategories];
                const secondary =
                    secondaryChoice === ORIGIN.SYNC || secondaryChoice === ORIGIN.IGNORE
                        ? syncSecondary
                        : plspSecondary;

                resolvedData.categories = [...(primary ? [primary] : []), ...secondary];
            }

            await API[profile.platform](profileId, {
                data: resolvedData,
                options: activeSelectedData,
            });
            await dispatch(accountActions.get());
            handleClose();
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            onRefetchData();
        } catch (error) {
            if (error.errorCode === 'google_validation_failed' && error.reasons?.length > 0) {
                error.reasons.forEach(reason => {
                    let message = reason;
                    if (reason === GOOGLE_RESTRICTED_FIELD_REASON) {
                        message = t('ProfilesPage.restrictedFieldHint', {
                            source: ProfileType[profile?.platform],
                        });
                    } else if (reason === GOOGLE_DESCRIPTION_URL_REASON) {
                        message = t('ProfilesPage.descriptionContainsUrlHint');
                    }
                    dispatch(alertActions.error(message));
                });
            } else {
                handleApiError(error, 'alertMessages.editFail');
            }
        }
        setIsSubmitting(false);
    };

    const TABLE_BODY_BASE = [
        {
            field: t('SettingsCompanies.locationName'),
            plsp: (
                <Text variant="body2" align="left">
                    {plspData.locationName || '-'}
                </Text>
            ),
            platform: (
                <Text variant="body2" align="left" hasDiff={plspData.locationName !== syncData.locationName}>
                    {syncData.locationName || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.generalInfo'),
            action: SyncDiff.LocationName,
        },
        {
            field: t('common.status'),
            plsp: (
                <Text variant="body2" align="left">
                    {plspData.status === COMPANY_STATUS.OPEN ? t('common.open') : t('common.closed')}
                </Text>
            ),
            platform: (
                <Text variant="body2" align="left" hasDiff={plspData.status !== syncData.status}>
                    {syncData.status === COMPANY_STATUS.OPEN ? t('common.open') : t('common.closed')}
                </Text>
            ),
            category: t('SettingsCompanies.generalInfo'),
            action: SyncDiff.Status,
        },
        {
            field: t('SettingsCompanies.primaryPhone'),
            plsp: (
                <Text variant="body2" align="left">
                    {formatPhoneNumber(plspData.phoneNumber) || '-'}
                    {isGoogle &&
                        plspData.phoneExtension &&
                        ` ${t('SettingsCompanies.phoneExtension')} ${plspData.phoneExtension || '-'}`}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={
                        plspData.phoneNumber !== syncData.phoneNumber ||
                        (isGoogle && plspData.phoneExtension !== syncData.phoneExtension)
                    }
                >
                    {formatPhoneNumber(syncData.phoneNumber) || '-'}
                    {isGoogle &&
                        syncData.phoneExtension &&
                        ` ${t('SettingsCompanies.phoneExtension')} ${syncData.phoneExtension || '-'}`}
                </Text>
            ),
            category: t('SettingsCompanies.contact'),
            action: SyncDiff.PhoneNumber,
        },
        ...(isGoogle
            ? [
                  {
                      field: t('SettingsCompanies.additionalPhones'),
                      plsp: renderValueList(plspAdditionalPhones, { format: formatPhoneNumber }),
                      platform: renderValueList(syncAdditionalPhones, {
                          format: formatPhoneNumber,
                          comparedTo: plspAdditionalPhones,
                      }),
                      category: t('SettingsCompanies.contact'),
                      action: SyncDiff.AdditionalPhones,
                  },
              ]
            : []),
        {
            field: t('common.email'),
            plsp: (
                <Text variant="body2" align="left">
                    {plspData.email || '-'}
                </Text>
            ),
            platform: (
                <Text variant="body2" align="left" hasDiff={plspData.email !== syncData.email}>
                    {syncData.email || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.contact'),
            action: SyncDiff.Email,
        },
        {
            field: t('SettingsCompanies.website'),
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.website || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.website !== syncData.website}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.website || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.contact'),
            action: SyncDiff.Website,
        },
        ...(isGoogle
            ? [
                  {
                      field: t('common.chat'),
                      plsp: (
                          <>
                              <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                                  {plspData.chatLink || '-'}
                              </Text>
                              <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                                  {plspData.chatType !== null
                                      ? plspData.chatType === ChatType.SMS
                                          ? 'SMS'
                                          : 'WhatsApp'
                                      : '-'}
                              </Text>
                          </>
                      ),
                      platform: (
                          <>
                              <Text
                                  variant="body2"
                                  align="left"
                                  hasDiff={plspData.chatLink !== syncData.chatLink}
                                  sx={{ wordBreak: 'break-all' }}
                              >
                                  {syncData.chatLink || '-'}
                              </Text>
                              <Text
                                  variant="body2"
                                  align="left"
                                  sx={{ wordBreak: 'break-all' }}
                                  hasDiff={plspData.chatType !== syncData.chatType}
                              >
                                  {syncData.chatType !== null
                                      ? syncData.chatType === ChatType.SMS
                                          ? 'SMS'
                                          : 'WhatsApp'
                                      : '-'}
                              </Text>
                          </>
                      ),
                      category: t('SettingsCompanies.contact'),
                      action: SyncDiff.Chat,
                  },
              ]
            : []),
        {
            field: t('SettingsCompanies.address'),
            plsp: plspData.hasBusinessAddress ? (
                <>
                    {isGoogle && (
                        <Text variant="body2" align="left">
                            {t('common.latitude')}: {plspData.latitude ? Number(plspData.latitude)?.toFixed(6) : '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text variant="body2" align="left">
                            {t('common.longitude')}: {plspData.longitude ? Number(plspData.longitude)?.toFixed(6) : '-'}
                        </Text>
                    )}
                    <Text variant="body2" align="left">
                        {isGoogle && `${t('SettingsCompanies.addressLine1')}:`} {plspData.addressLine1 || '-'}
                    </Text>
                    {isGoogle && (
                        <Text variant="body2" align="left">
                            {t('SettingsCompanies.addressLine2')}: {plspData.addressLine2 || '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text variant="body2" align="left">
                            {t('SettingsCompanies.city')}: {plspData.city || '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text variant="body2" align="left">
                            {t('SettingsCompanies.state')}: {plspData.region || '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text variant="body2" align="left">
                            {t('SettingsCompanies.postalCode')}: {plspData.postalCode || '-'}
                        </Text>
                    )}
                </>
            ) : (
                <Text variant="body2" align="left">
                    {t('ProfilesPage.noAddress')}
                </Text>
            ),
            platform: syncData.hasBusinessAddress ? (
                <>
                    {isGoogle && (
                        <Text
                            variant="body2"
                            align="left"
                            hasDiff={syncData.latitude !== plspData.latitude || !plspData.hasBusinessAddress}
                        >
                            {t('common.latitude')}: {syncData.latitude ? Number(syncData.latitude)?.toFixed(6) : '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text
                            variant="body2"
                            align="left"
                            hasDiff={syncData.longitude !== plspData.longitude || !plspData.hasBusinessAddress}
                        >
                            {t('common.longitude')}: {syncData.longitude ? Number(syncData.longitude)?.toFixed(6) : '-'}
                        </Text>
                    )}
                    <Text
                        variant="body2"
                        align="left"
                        hasDiff={syncData.addressLine1 !== plspData.addressLine1 || !plspData.hasBusinessAddress}
                    >
                        {isGoogle && `${t('SettingsCompanies.addressLine1')}:`} {syncData.addressLine1 || '-'}
                    </Text>
                    {isGoogle && (
                        <Text
                            variant="body2"
                            align="left"
                            hasDiff={syncData.addressLine2 !== plspData.addressLine2 || !plspData.hasBusinessAddress}
                        >
                            {t('SettingsCompanies.addressLine2')}: {syncData.addressLine2 || '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text
                            variant="body2"
                            align="left"
                            hasDiff={syncData.city !== plspData.city || !plspData.hasBusinessAddress}
                        >
                            {t('SettingsCompanies.city')}: {syncData.city || '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text
                            variant="body2"
                            align="left"
                            hasDiff={differingAddressParts.includes('region') || !plspData.hasBusinessAddress}
                        >
                            {t('SettingsCompanies.state')}: {syncData.region || '-'}
                        </Text>
                    )}
                    {isGoogle && (
                        <Text
                            variant="body2"
                            align="left"
                            hasDiff={differingAddressParts.includes('postalCode') || !plspData.hasBusinessAddress}
                        >
                            {t('SettingsCompanies.postalCode')}: {syncData.postalCode || '-'}
                        </Text>
                    )}
                </>
            ) : (
                <Text variant="body2" align="left" hasDiff={plspData.hasBusinessAddress}>
                    {t('ProfilesPage.noAddress')}
                </Text>
            ),
            category: t('SettingsCompanies.address'),
            action: SyncDiff.Address,
            pluspointOnly: !isGoogle,
        },
        ...(isGoogle
            ? [
                  {
                      field: t('SettingsCompanies.locatedIn'),
                      plsp: (
                          <Text variant="body2" align="left">
                              {renderLocatedIn(plspData.parentLocation)}
                          </Text>
                      ),
                      platform: (
                          <Text variant="body2" align="left" hasDiff={!sameParentLocation}>
                              {renderLocatedIn(syncData.parentLocation)}
                          </Text>
                      ),
                      category: t('SettingsCompanies.address'),
                      action: SyncDiff.ParentLocation,
                      notIgnorable: true,
                  },
              ]
            : []),
        {
            field: t('SettingsCompanies.businessHours'),
            plsp: !workingDays.length ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                workingDays.map(day => (
                    <Text variant="body2" align="left" key={day} sx={{ display: 'flex', gap: '4px' }}>
                        <span>{DAY_NAMES[day]}: </span>
                        <span>
                            {plslWorkingHours[day]
                                ? plslWorkingHours[day].map((h, index) => (
                                      <span key={index} style={{ display: 'block' }}>
                                          {h.from}-{h.to}
                                      </span>
                                  ))
                                : '-'}
                        </span>
                    </Text>
                ))
            ),
            platform: !workingDays.length ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                workingDays.map(day => (
                    <Text
                        key={day}
                        variant="body2"
                        sx={{ display: 'flex', gap: '4px' }}
                        hasDiff={!isEqual(plslWorkingHours[day], syncWorkingHours[day])}
                    >
                        <span>{DAY_NAMES[day]}: </span>
                        <span>
                            {syncWorkingHours[day]
                                ? syncWorkingHours[day].map((h, index) => (
                                      <span key={index} style={{ display: 'block' }}>
                                          {h.from}-{h.to}
                                      </span>
                                  ))
                                : '-'}
                        </span>
                    </Text>
                ))
            ),
            category: t('SettingsCompanies.businessHours'),
            action: SyncDiff.BusinessHours,
            unsendableHint:
                !isGoogle && !hoursAreSendable
                    ? t('ProfilesPage.noHoursToSendHint', { source: ProfileType[profile?.platform] })
                    : undefined,
        },
        {
            field: t('SettingsCompanies.specialHours'),
            plsp: !(plspData.specialHours || []).length ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                (plspData.specialHours || []).map(hour => {
                    const formattedDate = format(parseISO(hour.date), 'dd MMM yyyy');
                    return (
                        <Text variant="body2" align="left" key={hour.date} sx={{ display: 'flex', gap: '4px' }}>
                            <span>{formattedDate}: </span>
                            <span>{formatSpecialHour(hour)}</span>
                        </Text>
                    );
                })
            ),
            platform: !(syncData.specialHours || []).length ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                (syncData.specialHours || []).map(hour => {
                    const plspHour = plslSpecialHours[hour.date];
                    const formattedDate = format(parseISO(hour.date), 'dd MMM yyyy');
                    const hasDiff = !plspHour || formatSpecialHour(hour) !== formatSpecialHour(plspHour);
                    return (
                        <Text key={hour.date} variant="body2" sx={{ display: 'flex', gap: '4px' }} hasDiff={hasDiff}>
                            <span>{formattedDate}: </span>
                            <span>{formatSpecialHour(hour)}</span>
                        </Text>
                    );
                })
            ),
            category: t('SettingsCompanies.specialHours'),
            action: SyncDiff.SpecialHours,
        },
        {
            field: t('common.description'),
            plsp: (
                <Text variant="body2" align="left">
                    {plspData.description || '-'}
                </Text>
            ),
            platform: (
                <Text variant="body2" align="left" hasDiff={plspData.description !== syncData.description}>
                    {syncData.description || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.additionalInformation'),
            action: SyncDiff.Description,
        },
        {
            field: t('SettingsCompanies.openingDate'),
            plsp: (
                <Text variant="body2" align="left">
                    {plspData.openingDate
                        ? format(new Date(parseISO(plspData.openingDate)), 'dd MMM yyyy', {
                              locale: I18N_DATE_FORMAT[language],
                          })
                        : '-'}
                </Text>
            ),
            platform: (
                <Text variant="body2" align="left" hasDiff={plspData.openingDate !== syncData.openingDate}>
                    {syncData.openingDate
                        ? format(new Date(parseISO(syncData.openingDate)), 'dd MMM yyyy', {
                              locale: I18N_DATE_FORMAT[language],
                          })
                        : '-'}
                </Text>
            ),
            category: t('SettingsCompanies.additionalInformation'),
            action: SyncDiff.OpeningDate,
        },
        ...(isGoogle
            ? [
                  {
                      field: t('Listings.primaryCategory'),
                      plsp: (
                          <Text variant="body2" align="left">
                              {plspPrimaryCategory?.displayName || '-'}
                          </Text>
                      ),
                      platform: (
                          <Text
                              variant="body2"
                              align="left"
                              hasDiff={plspPrimaryCategory?.googleId !== syncPrimaryCategory?.googleId}
                          >
                              {syncPrimaryCategory?.displayName || '-'}
                          </Text>
                      ),
                      category: t('SettingsCompanies.categoriesAndServices'),
                      action: SyncDiff.PrimaryCategory,
                  },
                  {
                      field: t('Listings.secondaryCategory', { count: 0 }),
                      plsp: renderValueList(plspAdditionalCategories),
                      platform: renderValueList(syncAdditionalCategories, { comparedTo: plspAdditionalCategories }),
                      category: t('SettingsCompanies.categoriesAndServices'),
                      action: SyncDiff.SecondaryCategories,
                  },
              ]
            : []),
        {
            field: t('common.logo'),
            plsp: !plspData.logoUrl ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                <div style={{ display: 'flex' }}>
                    <Image referrerPolicy="no-referrer" src={plspData.logoUrl} alt="logo" />
                </div>
            ),
            platform: !syncData.logoUrl ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                <div style={{ display: 'flex' }}>
                    <Image referrerPolicy="no-referrer" src={syncData.logoUrl} alt="logo" />
                </div>
            ),
            category: t('common.appearance'),
            action: SyncDiff.Logo,
        },
        {
            field: t('SettingsCompanies.coverPhoto'),
            plsp: !plspData.coverPhotoUrl ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                <div style={{ display: 'flex' }}>
                    <Image referrerPolicy="no-referrer" src={plspData.coverPhotoUrl} alt="logo" />
                </div>
            ),
            platform: !syncData.coverPhotoUrl ? (
                <Text variant="body2" align="left">
                    -
                </Text>
            ) : (
                <div style={{ display: 'flex' }}>
                    <Image referrerPolicy="no-referrer" src={syncData.coverPhotoUrl} alt="logo" />
                </div>
            ),
            category: t('common.appearance'),
            action: SyncDiff.CoverPhoto,
            unsendableHint:
                !isGoogle && !plspData.coverPhotoUrl
                    ? t('ProfilesPage.noCoverToSendHint', { source: ProfileType[profile?.platform] })
                    : undefined,
        },
        {
            field: 'Facebook',
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.socialMediaLinks?.Facebook || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.socialMediaLinks?.Facebook !== syncData.socialMediaLinks?.Facebook}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.socialMediaLinks?.Facebook || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.onlineProfiles'),
            action: SyncDiff.Facebook,
        },
        {
            field: 'Instagram',
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.socialMediaLinks?.Instagram || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.socialMediaLinks?.Instagram !== syncData.socialMediaLinks?.Instagram}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.socialMediaLinks?.Instagram || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.onlineProfiles'),
            action: SyncDiff.Instagram,
        },
        {
            field: 'Youtube',
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.socialMediaLinks?.Youtube || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.socialMediaLinks?.Youtube !== syncData.socialMediaLinks?.Youtube}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.socialMediaLinks?.Youtube || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.onlineProfiles'),
            action: SyncDiff.Youtube,
        },
        {
            field: 'X',
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.socialMediaLinks?.X || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.socialMediaLinks?.X !== syncData.socialMediaLinks?.X}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.socialMediaLinks?.X || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.onlineProfiles'),
            action: SyncDiff.X,
        },
        {
            field: 'LinkedIn',
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.socialMediaLinks?.LinkedIn || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.socialMediaLinks?.LinkedIn !== syncData.socialMediaLinks?.LinkedIn}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.socialMediaLinks?.LinkedIn || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.onlineProfiles'),
            action: SyncDiff.LinkedIn,
        },
        {
            field: 'Pinterest',
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.socialMediaLinks?.Pinterest || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.socialMediaLinks?.Pinterest !== syncData.socialMediaLinks?.Pinterest}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.socialMediaLinks?.Pinterest || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.onlineProfiles'),
            action: SyncDiff.Pinterest,
        },
        {
            field: 'Tiktok',
            plsp: (
                <Text variant="body2" align="left" sx={{ wordBreak: 'break-all' }}>
                    {plspData.socialMediaLinks?.Tiktok || '-'}
                </Text>
            ),
            platform: (
                <Text
                    variant="body2"
                    align="left"
                    hasDiff={plspData.socialMediaLinks?.Tiktok !== syncData.socialMediaLinks?.Tiktok}
                    sx={{ wordBreak: 'break-all' }}
                >
                    {syncData.socialMediaLinks?.Tiktok || '-'}
                </Text>
            ),
            category: t('SettingsCompanies.onlineProfiles'),
            action: SyncDiff.Tiktok,
        },
    ];

    const FILTERED_BODY = TABLE_BODY_BASE.filter(item => currentDiff.includes(item.action));

    return (
        <Modal
            fullScreen
            isOpen={isOpen}
            handleClose={handleClose}
            title={t('ProfilesPage.resolveTitle')}
            subtitle={t('ProfilesPage.resolveSubtitle')}
            onPrimaryAction={onSubmit}
            primaryActionText={t('buttons.save')}
            onSecondaryAction={handleClose}
            secondaryActionText={t('buttons.cancel')}
            primaryActionDisabled={
                isSubmitting ||
                FILTERED_BODY.length === 0 ||
                FILTERED_BODY.every(item => selectedData[item.action] === seededData[item.action])
            }
        >
            <TableContainer>
                <Table
                    withHover
                    columnNames={getTableHeader(t, ProfileType[profile?.platform])}
                    rows={getTableBody(
                        FILTERED_BODY,
                        ProfileType[profile?.platform],
                        selectedData,
                        setSelectedData,
                        fbConnected,
                        profile?.restrictedSyncFields || [],
                        profile?.pushRejections || [],
                        t,
                    )}
                />
            </TableContainer>
        </Modal>
    );
};

export default ResolveModal;
